import React, { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { toast } from "react-toastify";

import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Rating,
  Typography,
  useTheme
} from "@mui/material";

import { CreateReviewDialog } from '../components/CreateReviewDialog';

import { RatingIcon, StarIcon } from "../../../components/icons";
import { IPilotReviewProps } from "../types/reviews";
import { tokens } from "../../../theme";
import { paginate } from '../../../utils/paginate';
import { PilotProps } from "../types/view-pilot";
import { Stream } from "../../../types/streams";
import { IReviewFormProps } from "../types/reviews";
import apiService from "../../../service/Api";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import { User } from "../../users-admin/types/User";
import SearchStreamDialog from "../components/SearchStreamDialog";
import { AxiosError } from "axios";

interface PilotReviewProps {
  pilot: PilotProps | null;
};

interface Accumulator {
  [key: number]: number;
};

export const PilotReviews: React.FC<PilotReviewProps> = ({ pilot }) => {
  const [reviews, setReviews] = useState<IPilotReviewProps[]>([]);
  const [totalReviews, setTotalReviews] = useState<number>(0);
  const [averageRating, setAverageRating] = useState<number>(0);
  const [searchEventOpen, setSearchEventOpen] = useState(false);
  const [createReviewOpen, setCreateReviewOpen] = useState<boolean>(false);
  const [formData, setFormData] = useState<IReviewFormProps>({
    rater: undefined,
    rating: 0,
    stream: null,
    stream_id: null,
    comment: ''
  });
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<Stream | null>();
  const [ratingDistribution, setRatingDistribution] = useState<Accumulator>();
  const [page, setPage] = useState(1);
  const [perPage] = useState(3);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const { id } = useParams<{ id: string }>();
  const user = useSelector((state: RootState) => state.auth.user) as User | null;

  const paginated = paginate(reviews, page, perPage);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
    
  const backgroundColor = colors.primary[800] || "#A9B2C3";

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    setError(null);
    setWarning(null);

    try {
      const response = await apiService.get<{ results: IPilotReviewProps[] }>(
        `/api/v1/crews/crew-ratings/?crew_member=${id}`, { page_size: 100 });

      setReviews(response.data.results.filter(rev => {
        return rev.crew_member.toString() === id
      }));
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data?.message || "Failed to load reviews. Please try again.";
        setError(errorMessage);
      } else {
        console.log({ error }, 'fetchReviews error');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (pilot) {
      fetchReviews();
    }
  }, [fetchReviews, pilot]);

  useEffect(() => {
    if (reviews.length) {
      getRatingDistribution(reviews);
      setTotalReviews(reviews.length);
      getAverageRating(reviews);
    }
  }, [reviews]);

  const getRatingDistribution = (reviews: IPilotReviewProps[]) => {
    const distro = reviews.reduce((acc: Accumulator, curr: IPilotReviewProps) => {
      const rating: number = curr.rating;
      acc[rating] = (acc[rating] ?? 0) + 1;
      return acc;
    }, {});

    setRatingDistribution(distro);
  };

  const getAverageRating = (reviews: IPilotReviewProps[]) => {
    let grandTotal = 0;
    reviews.forEach(r => {
      grandTotal += r.rating;
    })
    setAverageRating(Math.round(grandTotal / reviews.length));
  };

  const getRatingProgress = (rtgs: number) => {
    const progress = Math.round((rtgs / totalReviews) * 100);
    return progress;
  };

  const handleCommentChange = (value: string) => {
    setFormData({
      ...formData,
      comment: value,
    });
  };

  const handleCreateReview = async () => {
    setFormLoading(true);
    try {
      const { rating } = formData;

      if (rating === 0) {
        toast.error("Please provide a rating before proceeding.");
        return;
      };

      if (user?.id === pilot?.user_id) {
        toast.error(`You can't give yourself a rating.`);
        return;
      };

      const payload = {
        crew_member: pilot?.id,
        crew_member_detail: {
          user: {
            email: pilot?.email,
            username: pilot?.username,
          },
        },
        stream: formData.stream,
        stream_id: formData.stream_id,
        crew_member_id: pilot?.id,        
        rater: formData.rater,
        rater_detail: {
          email: user?.username,
          username: user?.username,
          company: {
            name: 'Muzukuru',
          },
        },
        rater_id: formData.rater,
        rating: formData.rating,
        comment: formData.comment
      };

      await apiService.post<{ results: IPilotReviewProps[] }>(
        "/api/crews/crew-ratings/", payload);

      fetchReviews();

      toast.success("Thank you. Feedback received!", {
        position: "top-right",
        autoClose: 2000,
        theme: "light",
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.message || "Failed to create a review. Please try again.", {
          position: "top-right",
          autoClose: 2000,
          theme: "light",
        });
      } else {
        console.log('Failed to create a review', error);
      }
    } finally {
      setFormLoading(false);
      setCreateReviewOpen(false);
      resetForm();
    }
  };

  const resetForm = () => {
    setFormData({
      rater: undefined,
      rating: 0,
      stream: null,
      stream_id: null,
      comment: ''
    });
  };

  if (!pilot) return null;

  return (
    <Box sx={{ p: 1, minHeight: "auto" }}>
      <Grid container spacing={2}>
        {/* Ratings Distribution */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ boxShadow: 0, height: "100%" }}>
            <CardContent>
              <Box
                display={'flex'}
                alignItems={'center'}
                sx={{ pb: '8px', borderBottom: 1, borderColor: 'divider', mb: '8px' }}
                gap={'4px'}>
                <StarIcon color="disabled" sx={{ fontSize: 20 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Ratings Distribution
                </Typography>
              </Box>

              <Box display={'flex'} alignItems={'center'} sx={{ mb: 1 }} gap={1}>
                <Rating size={'small'} value={averageRating} precision={0.1} readOnly />
                <Box display={'flex'} alignItems={'center'} gap={0.2}>
                  <Typography variant={'subtitle2'}>{averageRating}</Typography>
                  <Typography variant={'subtitle2'}>{`(${totalReviews} Reviews)`}</Typography>
                </Box>
              </Box>
              
              {ratingDistribution && (
                <Box sx={{ mt: 2 }}>
                  <Grid container sx={{ mb: 0.4 }} spacing={'4px'}>
                    <Grid size={1}>
                      <Typography variant={'body1'} sx={{ fontWeight: 600 }}>5</Typography>
                    </Grid>
                    <Grid size={10} sx={{ py: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={getRatingProgress(ratingDistribution ? ratingDistribution[5] : 0)}
                      />
                    </Grid>
                    <Grid size={1}>
                      <Typography variant={'body2'}>{`(${ratingDistribution[5] ? ratingDistribution[5] : 0})`}</Typography>
                    </Grid>
                  </Grid>

                  <Grid container sx={{ mb: 0.4 }} spacing={'4px'}>
                    <Grid size={1}>
                      <Typography variant={'body1'} sx={{ fontWeight: 600 }}>4</Typography>
                    </Grid>
                    <Grid size={10} sx={{ py: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={getRatingProgress(ratingDistribution ? ratingDistribution[4] : 0)}
                      />
                    </Grid>
                    <Grid size={1}>
                      <Typography variant={'body2'}>{`(${ratingDistribution[4] ? ratingDistribution[4] : 0})`}</Typography>
                    </Grid>
                  </Grid>

                  <Grid container sx={{ mb: 0.4 }} spacing={'4px'}>
                    <Grid size={1}>
                      <Typography variant={'body1'} sx={{ fontWeight: 600 }}>3</Typography>
                    </Grid>
                    <Grid size={10} sx={{ py: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={getRatingProgress(ratingDistribution ? ratingDistribution[3] : 0)}
                      />
                    </Grid>
                    <Grid size={1}>
                      <Typography variant={'body2'}>{`(${ratingDistribution[3] ? ratingDistribution[3] : 0})`}</Typography>
                    </Grid>
                  </Grid>

                  <Grid container sx={{ mb: 0.4 }} spacing={'4px'}>
                    <Grid size={1}>
                      <Typography variant={'body1'} sx={{ fontWeight: 600 }}>2</Typography>
                    </Grid>
                    <Grid size={10} sx={{ py: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={getRatingProgress(ratingDistribution ? ratingDistribution[2] : 0)}
                      />
                    </Grid>
                    <Grid size={1}>
                      <Typography variant={'body2'}>{`(${ratingDistribution[2] ? ratingDistribution[2] : 0})`}</Typography>
                    </Grid>
                  </Grid>
                  
                  <Grid container sx={{ mb: 0.4 }} spacing={'4px'}>
                    <Grid size={1}>
                      <Typography variant={'body1'} sx={{ fontWeight: 600 }}>1</Typography>
                    </Grid>
                    <Grid size={10} sx={{ py: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={getRatingProgress(ratingDistribution ? ratingDistribution[1] : 0)}
                      />
                    </Grid>
                    <Grid size={1}>
                      <Typography variant={'body2'}>{`(${ratingDistribution[1] ? ratingDistribution[1] : 0})`}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Pilot Reviews */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={{ boxShadow: 0, height: "100%" }}>
            <CardContent>
              <Box
                display={'flex'}
                alignItems={'center'}
                justifyContent={'space-between'}
                sx={{ pb: '8px', borderBottom: 1, borderColor: 'divider', mb: '8px' }}
              >
                <Box
                  display={'flex'}
                  alignItems={'center'}
                  gap={1}>
                  <RatingIcon color="disabled" sx={{ fontSize: 20 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Recent Reviews
                  </Typography>
                </Box>
                {user ? (
                  <Button
                    size={"small"}
                    disabled={parseInt(user.id) === pilot.user_id}
                    onClick={() => setSearchEventOpen(true)}
                    sx={{
                      textTransform: "capitalize",
                    }}
                    variant="contained"
                  >
                    Add Review
                  </Button>
                ) : null}
              </Box>

              {loading ? (
                <CircularProgress color={'primary'} size={16} />
              ) : error ? (
                <Alert severity="error">{error}</Alert>
              ) : warning ? (
                <Alert severity="warning">{warning}</Alert>
              ) : reviews?.length === 0 ? (
                <Chip
                  size={"small"}
                  sx={{ borderRadius: 1, textTransform: "uppercase" }}
                  label={<i>No reviews for {pilot.name} yet</i>}
                />
              ) : (
                <Box>
                  {reviews && paginated?.map((rev: IPilotReviewProps) => {
                    return (
                      <Box
                        key={rev.id}
                        sx={{
                          border: '1px solid',
                          borderRadius: 2,
                          mb: 1,
                          padding: 1,
                          borderColor: backgroundColor
                        }}>
                          <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                            <Box display={'flex'} alignItems={'center'} gap={'4px'}>
                              <Avatar
                                src={rev.rater_summary?.profile_picture || ''}
                                sx={{
                                  width: 32,
                                  height: 32
                                }}
                              />
                              <Box>
                                <Typography variant={'subtitle2'} sx={{ mb: -0.8 }}>
                                  {rev.rater_summary
                                    ? `${rev.rater_summary.first_name} ${rev.rater_summary.last_name}`
                                    : "Unknown rater"}
                                </Typography>
                                <Typography variant={'caption'}>{rev.rater_summary?.role || "N/A"}</Typography>
                              </Box>
                            </Box>
                            <Rating size="small" value={rev.rating} precision={1} readOnly />
                          </Box>
                          <Box sx={{ mt: 1 }}>
                            <Typography variant={'caption'}>{format(new Date(rev.created_at), "dd/MM/yyyy")}</Typography>
                            <Typography variant={'body2'}>{rev.comment}</Typography>
                            {rev.stream_detail && (
                              <Chip
                                label={`Stream: ${rev.stream_detail.title}`}
                                size={'small'}
                                sx={{
                                  mt: 0.8,
                                  borderRadius: 1,
                                  bgcolor: backgroundColor
                                }}
                              />
                            )}
                          </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
              
              {reviews.length !== 0 && (
                <Box sx={{ mt: 2 }}>
                  <Box display={'flex'} alignItems={'center'} gap={.4}>
                    <Typography sx={{ cursor: 'pointer', fontWeight: 600 }} variant={'subtitle1'}>Page:</Typography>
                    {[...Array(Math.ceil(reviews.length / perPage)).keys()].map(pg => (
                    <Box
                      key={pg}
                      onClick={() => setPage(pg + 1)}
                      sx={{
                        bgcolor: page === pg + 1 ? '#724E41' : colors.primary[900],
                        borderRadius: 1,
                        cursor: 'pointer',
                        px: 1.6,
                        py: .4,
                        fontSize: 14,
                        fontWeight: page === pg + 1 ? 500 : 600,
                        color: page === pg + 1 ? '#FFF' : colors.primary[700],
                        transition: 'all .4s ease'
                      }}>
                        <Typography sx={{ cursor: 'pointer' }} variant={'h6'}>{pg + 1}</Typography>
                    </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialogs */}

      <SearchStreamDialog
        user={{
          id: pilot.user_id,
          name: `${pilot.name} ${pilot.surname}`
        }}
        searchEventOpen={searchEventOpen}
        setSearchEventOpen={setSearchEventOpen}
        searchResults={searchResults}
        setSearchResults={setSearchResults}
        setCreateReviewOpen={setCreateReviewOpen}
      />

      <CreateReviewDialog
        createReviewOpen={createReviewOpen}
        formData={formData}
        event={searchResults}
        formLoading={formLoading}
        handleCreateReview={handleCreateReview}
        handleCommentChange={handleCommentChange}
        pilot={pilot}
        resetForm={resetForm}
        setCreateReviewOpen={setCreateReviewOpen}
        setFormData={setFormData}
      />
    </Box>
  );
};
